<template>
  <v-modal name="devices" @callback="modalCallback" @modalHide="clear">
    <section class="devices-edit">
      <header>
        <h4>
          <v-icon name="plus"></v-icon>
          {{ $t('Add Devices') }}
        </h4>
      </header>

      <v-tabs>
        <v-tab :name="$t('From Scan')">
          <v-device-list :devices="devices" @add-devices="addDevices($event, false, () => searchDescriptions($event))">
            <v-promise-button class="dark rounded small icon" @click="scan" :disabled="!connection.canScan">
              <template slot="loading">
                <v-loader></v-loader>
                {{ $t('Scanning') }}
              </template>
              <template slot="success">
                <v-icon name="check"></v-icon>
                {{ $t('Completed') }}
              </template>
              <template slot="error">
                <v-icon name="x"></v-icon>
                {{ $t('Failed') }}
              </template>
              <v-icon name="crosshair"></v-icon>
              {{ $t('Scan Bus') }}
            </v-promise-button>
            <v-checkbox v-model="autoApplyDescriptions" @change="updateAutoApplyDescriptions">
              {{ $t('Apply ESI from database') }}
            </v-checkbox>
          </v-device-list>
        </v-tab>

        <v-tab :name="$t('From Configuration')">
          <v-device-list :devices="configDevices" @add-devices="addDevices">
            <v-file class="secondary rounded small icon" accept="text/xml" @change.native="loadConfigFile">
              <v-icon name="upload"></v-icon>
              {{ $t('Upload File') }}
            </v-file>

            <v-promise-button class="dark rounded small icon" @click="scanConfigFile" :disabled="!connection.canScan">
              <template slot="loading">
                <v-loader></v-loader>
                {{ $t('Scanning') }}
              </template>
              <template slot="success">
                <v-icon name="check"></v-icon>
                {{ $t('Completed') }}
              </template>
              <template slot="error">
                <v-icon name="x"></v-icon>
                {{ $t('Failed') }}
              </template>
              <v-icon name="crosshair"></v-icon>
              {{ $t('Fetch') }}
            </v-promise-button>
          </v-device-list>
        </v-tab>

        <v-tab :name="$t('From Description Files')">
          <v-device-list
            :devices="ESIDevices"
            :is-editable="true"
            @edit-device="editDevice"
            @add-devices="addDevices($event, true)"
            @remove-devices="removeDevices"
          >
            <v-file class="dark rounded small icon" accept="text/xml" @change.native="loadFromEsiFiles" multiple>
              <v-icon name="upload"></v-icon>
              {{ $t('Upload Files') }}

              <v-loader v-if="isLoading"></v-loader>
            </v-file>
          </v-device-list>
          <v-modal name="select-devices" @modalHide="clearESIDevices">
            <v-devices-select :devices="ESIDevices" @select-devices="addEsiDevices"></v-devices-select>
          </v-modal>
        </v-tab>

        <v-tab :name="$t('Manually')">
          <v-device-list
            :devices="manualDevices"
            @edit-device="editDevice"
            @add-devices="addDevices($event)"
            :is-editable="true"
            @remove-devices="removeDevices"
          >
            <button class="success small rounded" @click="modal.show('create-device')">
              <v-icon name="plus"></v-icon>
              {{ $t('Device') }}
            </button>
          </v-device-list>
        </v-tab>
      </v-tabs>

      <v-modal name="create-device">
        <v-device-edit :device="selectedDevice" @create-device="createDevice"></v-device-edit>
      </v-modal>
    </section>
  </v-modal>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useModal, useFlits, useDialog } from '@vectioneer/ui'
import { useRouter } from 'vue-router/composables'
import { $t } from '/@/i18n'
import { useStore } from '/@/shared/composables/store'
import { EthercatDevice } from '/@/shared/entities/EthercatDevice'
import { EthercatDomain } from '/@/shared/entities/EthercatDomain'
import ConfigFileAdapter from '/@/shared/adapters/device-list/configuration/FileAdapter'
import ConfigScanAdapter from '/@/shared/adapters/device-list/configuration/ScanAdapter'
import DescriptionFileAdapter from '/@/shared/adapters/device-description/FileAdapter'
import ScanAdapter from '/@/shared/adapters/device-list/ScanAdapter'
import SearchAndProcessDescriptions from '/@/shared/actions/SearchAndProcessDescriptions'
import VDeviceList from './device-list/index.vue'
import VDeviceEdit from './edit/index.vue'
import VDevicesSelect from './select/index.vue'
import ESIWarning from '/@/shared/components/esi-warning/index.vue'

const { app, ethercat } = useStore()
const modal = useModal()
const flits = useFlits()
const dialog = useDialog()
const router = useRouter()

const isLoading = ref(false)
const devices = ref<EthercatDevice[]>([])
const configDevices = ref<EthercatDevice[]>([])
const ESIDevices = ref<EthercatDevice[]>([])
const manualDevices = ref<EthercatDevice[]>([])

const selectedDevice = ref<EthercatDevice | null>(null)
const domain = ref<EthercatDomain | null>(null)
const afterDevice = ref<EthercatDevice | null>(null)

const autoApplyDescriptions = ref(false)

const connection = computed(() => app.connectionParameters)

const modalCallback = ({ domain: modalDomain, device }: { domain?: EthercatDomain; device?: EthercatDevice }) => {
  if (modalDomain) {
    domain.value = modalDomain
  }

  if (device) {
    afterDevice.value = device
  }
}

const scan = async () => {
  isLoading.value = true

  try {
    devices.value = await new ScanAdapter().process(connection.value.host)
  } catch (error) {
    flits.error(error)
    throw error
  } finally {
    isLoading.value = false
  }
}

const loadConfigFile = async (event: InputEvent) => {
  const target = event.target as HTMLInputElement
  const file = (target.files as FileList).item(0)

  if (!file) {
    return
  }

  try {
    configDevices.value = await new ConfigFileAdapter().process(file)
  } catch (error) {
    flits.error(error)
    throw error
  } finally {
    target.value = ''
  }
}

const scanConfigFile = async () => {
  isLoading.value = true

  try {
    configDevices.value = await new ConfigScanAdapter().process(connection.value)
  } catch (error) {
    flits.error(error)
    throw error
  } finally {
    isLoading.value = false
  }
}

const loadFromEsiFiles = async (event: InputEvent) => {
  const target = event.target as HTMLInputElement
  const files = target.files as FileList
  const adapter = new DescriptionFileAdapter()

  isLoading.value = true

  try {
    const promises = Array.from(files).map(async (file) => {
      return await adapter.process(file)
    })

    ESIDevices.value = (await Promise.all(promises)).flat(1).sort((a, b) => a.id - b.id)
    modal.show('select-devices')
  } catch (error) {
    flits.error(error)
    throw error
  } finally {
    target.value = ''
    isLoading.value = false
  }
}

const addEsiDevices = (devices: EthercatDevice[]) => {
  modal.hide('select-devices')
  ESIDevices.value = devices
}

const editDevice = (device: EthercatDevice) => {
  selectedDevice.value = device
  modal.show('create-device')
}

const redirectToFirstDomain = () => {
  const firstDomain = ethercat.domains[0]
  if (firstDomain) {
    router.push(`/domain/${firstDomain.id}`)
  }
}

const addDevices = (deviceList: EthercatDevice[], autogeneratePosition: boolean = false, callback?: () => void) => {
  ethercat.addDevices({
    devices: deviceList,
    domain: domain.value,
    after: afterDevice.value,
    autogeneratePosition,
  })
  modal.hide('devices')

  if (!callback) {
    return redirectToFirstDomain()
  }

  callback()
}

const searchDescriptions = async (deviceList: EthercatDevice[]) => {
  if (!deviceList.length || !autoApplyDescriptions.value) {
    return
  }

  flits.info('Applying descriptions')

  const updatedDevices = (await SearchAndProcessDescriptions.run(deviceList)) as EthercatDevice[]

  if (updatedDevices?.length) {
    const names = updatedDevices //
      .map((device) => device.name)
      .filter((name) => name !== undefined)

    flits.success(`Descriptions updated for ${names.join(', ')}`)
  }
}

const createDevice = (device: EthercatDevice) => {
  manualDevices.value.push(device)
}

const removeDevices = (deviceList: EthercatDevice[]) => {
  ESIDevices.value = ESIDevices.value.filter((device) => !deviceList.includes(device))
  manualDevices.value = manualDevices.value.filter((device) => !deviceList.includes(device))
}

const clearESIDevices = () => {
  ESIDevices.value = []
}

const clear = () => {
  devices.value = []
  ESIDevices.value = []
  manualDevices.value = []
  configDevices.value = []
  domain.value = null
  afterDevice.value = null
}

const displayESIWarning = async () => {
  dialog.updateState({
    type: 'confirm',
  })

  const confirm = await dialog.component(ESIWarning, { plural: true })
  return confirm
}

const updateAutoApplyDescriptions = async () => {
  if (!autoApplyDescriptions.value) {
    return
  }

  const confirm = await displayESIWarning()

  if (!confirm) {
    autoApplyDescriptions.value = false
    return
  }

  autoApplyDescriptions.value = true
}
</script>

<style src="./style.css"></style>
